import {
  allPass,
  compose,
  add,
  lt,
  not,
  gt,
  reduce,
  __,
  takeLast,
  repeat,
} from 'ramda';

import { DeviceInfo } from '@ezugi/utils';

import {
  MAX_COLUMNS_COUNT,
  MAX_ROWS_COUNT,
  EMPTY_COLUMNS_COUNT,
  EMPTY_CELL,
  HANDHELD_MAX_COLUMNS_COUNT,
} from './constants';

export const isTriple = ([ DiceOne, DiceTwo, DiceThree ]) => DiceOne === DiceTwo && DiceOne === DiceThree;
export const isEven = (result) => result.reduce((a, b) => a + b, 0) % 2 === 0;

export const isSmall = allPass([
  compose(not, isTriple),
  compose(lt(__, 11), reduce(add, 0)),
]);

export const isBig = allPass([
  compose(not, isTriple),
  compose(gt(__, 10), reduce(add, 0)),
]);

function addEmptyCells(column) {
  Array.from({ length: MAX_ROWS_COUNT }, (_, idx) => idx).forEach((idx) => {
    column[idx] = column[idx] || EMPTY_CELL;
  });
}

function getFirstEmptyColumnIndex(columns, columnIndex) {
  const index = columns.findIndex(
    (column) => column[0] && column[0].type === EMPTY_CELL.type
  );
  return index !== -1 ? index : columnIndex;
}

function getFirstEmptyCellIndex(column) {
  return column.findIndex((cell) => cell && cell.type === EMPTY_CELL.type);
}

function equals(curr, prev) {
  return prev && curr.type === prev.type;
}

const EMPTY_COLUMN = Array.from({ length: MAX_ROWS_COUNT }, () => EMPTY_CELL);
const columnsCount = DeviceInfo.isHandheld ? HANDHELD_MAX_COLUMNS_COUNT : MAX_COLUMNS_COUNT;

function getColumnsFromItems(items) {
  const columns = [];

  let columnIndex = 0;
  let cellIndex = 0;
  let isSnake = false;
  let firstSnakeStartId;
  let columnsWithSnakes = []; // indexes of the columns that contain snakes

  items.forEach((current, index) => {
    if (!columns[columnIndex]) columns[columnIndex] = [];
    addEmptyCells(columns[columnIndex]);

    const previous = items[index - 1];
    const equalsPrevious = equals(current, previous);
    const isFullColumn = columns[columnIndex].every(
      (cell) => cell?.type !== EMPTY_CELL.type
    ) && columns[columnIndex].length === MAX_ROWS_COUNT;

    // Reset snake if items are different
    if (!equalsPrevious) isSnake = false;

    // If column is full or items differ, increment columns
    if (isFullColumn || (!equalsPrevious && !isSnake)) {
      columnIndex += 1;
      columns[columnIndex] = columns[columnIndex] || [];
      addEmptyCells(columns[columnIndex]);
    }

    if (equalsPrevious && isSnake) {
      columnIndex += 1;
      columns[columnIndex] = columns[columnIndex] || [];
      addEmptyCells(columns[columnIndex]);
      columns[columnIndex].splice(cellIndex, 1, current);
      columnsWithSnakes.push(columnIndex);

      return;
    }

    const isNextCellEmpty = columns[columnIndex][cellIndex + 1] === EMPTY_CELL;

    // Start snake road
    if (equalsPrevious && (!isNextCellEmpty || isFullColumn)) {
      isSnake = true;
      if (firstSnakeStartId === undefined) {
        // the index in `items` array of the first item of the first snake
        firstSnakeStartId = index - MAX_ROWS_COUNT;
      }
    }

    columnIndex = equalsPrevious && isNextCellEmpty
      ? columnIndex
      : getFirstEmptyColumnIndex(columns, columnIndex);

    if (equalsPrevious && (!isNextCellEmpty || isFullColumn)) {
      columnsWithSnakes.push(columnIndex);
    }

    // Use the previous items' location
    const keepCellIndex = equalsPrevious && (isFullColumn || isSnake || !isNextCellEmpty);

    cellIndex = keepCellIndex
      ? cellIndex
      : getFirstEmptyCellIndex(columns[columnIndex]);

    columns[columnIndex].splice(cellIndex, 1, current);
  });
  const columnsWithSnakesAdj = Math.min(0, columnsCount - columns.length - EMPTY_COLUMNS_COUNT);
  columnsWithSnakes = columnsWithSnakes.map((columnId) => columnId + columnsWithSnakesAdj);

  return { columns, columnsWithSnakes, firstSnakeStartId };
}

/** Map through all round results and generate a matrix of the roadmap */
export const generateColumnMatrix = (items) => {
  const { columns, columnsWithSnakes, firstSnakeStartId } = getColumnsFromItems(items);

  // Add mandatory EMPTY_COLUMNS
  const result = takeLast(
    columnsCount,
    columns.concat(repeat(EMPTY_COLUMN, EMPTY_COLUMNS_COUNT))
  );

  // Add empty columns if length < MAX_COLUMNS_COUNT
  if (result.length < columnsCount) {
    return result.concat(
      repeat(EMPTY_COLUMN, columnsCount - result.length)
    );
  }

  // a 'partial snake' is a snake not entirely visible in the roadmap (because its leftmost part was shifted outside of the roadmap)
  // we have a 'partial snake' if column 0 (the leftmost visible column) contains part of a snake
  const hasPartialSnake = columnsWithSnakes.includes(0);

  if (hasPartialSnake) {
    // if there's a 'partial snake', we'll remove the first snake completely from the roadmap and re-draw the roadmap
    const snakeStartItem = items[firstSnakeStartId];
    const firstDifferentTypeId = items.findIndex((item, id) => id > firstSnakeStartId && !equals(item, snakeStartItem));
    const firstSnakeEndId = (firstDifferentTypeId === -1 ? items.length : firstDifferentTypeId) - 1;
    const itemsAfterFirstSnake = items.slice(firstSnakeEndId + 1);
    return generateColumnMatrix(itemsAfterFirstSnake);
  }

  return result;
};
